import {LimitOffsetPagination} from '@/api/paginatedResults';
import {apolloPlatformAxiosInstance as axiosInstance} from './apolloPlatformAxiosInstance';
import type {Role} from '@/api/apolloPlatform/user';

export type SourceSystem = {
  id: number;
  name: string;
  region: string;
  identifier: string;
  description: string;
  customer_mappings?: number; // N.B reverse mapped for Ekco user's view of all source systems
};

export type CustomerMapping = {
  id: number;
  name: string;
  identifier: string;
  extra_info: string;
  source_system: SourceSystem;
  customer: Customer;
  created: string;
  updated: string;
};

export type Customer = {
  id: number;
  name: string;
  debtor_number: string;
  active: boolean;
  url: string;
  class_name: string;
};

export type DemoCustomer = {
  id: number;
  name: string;
  debtor_number: string;
  active: boolean;
  active_until: string | null;
};

export type UserRelation = {
  id: number | string;
  customer_id: number;
  customer: string;
  debtor_number: string;
  user_id: number | null;
  username: string;
  email: string;
  roles: Role[]; // Represents a UM role
  created: string;
  active: boolean;
  status: 'active' | 'inactive' | 'pending';
  // Only present when status is 'pending', see get_users in customers/api/views.py
  invitation_id?: number;
  key_expired?: boolean;
};

/** @see {@link apollo_platform/apps/customers/models.py} > UserRelation.Role */
export type UserRelationRole = {
  name: string;
  value: string;
  internal: boolean;
};

export type UserRelationRoleCreate = {
  user_id: number;
  customer_id: number;
  active: boolean;
  role: string;
};

/** New contacts to separate Django roles with permissions from those without */
export type Contact = {
  customer_name: string;
  debtor_number: string;
  user_name: string;
  contact_type: number;
  contact_type_name: string;
  email: string;
  linkedin_url: string;
  twitter_url: string;
  phone_number: string;
  avatar: string | null;
  id: number;
  user: number;
};

export type ContactCreate = {
  contact_type: number;
  customer: number;
  user: number;
};

export type ContactType = {
  name: string;
  id: number;
  class_name: string;
  url: string;
};

export type SelectedCustomer = {
  customer: Customer;
  contacts: Contact[];
};

export type CustomerStatus = {
  users: number;
  products: number;
  product_gaps: number;
  /** Only present for customers with a managed services product, which are expected to have a contract uploaded. */
  missing_contract?: boolean;
  /** The page (in the user's language) where the contract documents are uploaded, if there is one. */
  contract_page_id?: number | null;
};

export type PaginatedCustomerMapping = LimitOffsetPagination<CustomerMapping[]>;
export type PaginatedSourceSystem = LimitOffsetPagination<SourceSystem[]>;

/** @see {@link apollo_platform/apps/customers/api/serializers.py} > Customer360Serializer */
export type CustomerDiscoveryResult = {
  id: number;
  name: string;
  debtor_number: string;
  active: boolean;
  url: string;
  class_name: string;
  region: string;
  product_count: number;
  user_count: number;
  last_activity: string | null;
  /** Sum of the daily active customer users over the last 90 days */
  active_user_days: number;
};

export type PaginatedCustomerDiscoveryResult = LimitOffsetPagination<CustomerDiscoveryResult[]>;

/** @see {@link apollo_platform/apps/customers/api/serializers.py} > CustomerUserDiscoverySerializer */
export type CustomerUserDiscoveryResult = {
  /** UserRelation id: one row per user-customer pair */
  id: number;
  user_id: number;
  /** Empty when the user has no name set */
  name: string;
  /** Empty when the user has no email set */
  email: string;
  customer_id: number;
  customer_name: string;
  roles: Array<{id: number; name: string; type: string}>;
  last_activity: string | null;
  /** Days the user was active in the last 90 days */
  active_days: number;
};

export type PaginatedCustomerUserDiscoveryResult = LimitOffsetPagination<
  CustomerUserDiscoveryResult[]
>;

/** @see {@link apollo_platform/apps/customers/models.py} > Product */
export type CustomerProduct = {
  id: number;
  name: string;
};

export type PaginatedCustomerProduct = LimitOffsetPagination<CustomerProduct[]>;

/** @see {@link apollo_platform/apps/customers/api/filters.py} > CustomerProductFilterSet */
export type CustomerDiscoveryFilters = {
  limit?: number;
  offset?: number;
  search?: string;
  demo?: boolean;
  productsIn?: string;
  productsNotIn?: string;
  productsIsnull?: boolean;
  productsCount?: number;
  productsCountGt?: number;
  productsCountLt?: number;
  hasUsers?: boolean;
  hasActiveInvitations?: boolean;
  hasExpiredInvitations?: boolean;
  userCount?: number;
  userCountGt?: number;
  userCountLt?: number;
  activeUserDays?: number;
  activeUserDaysGt?: number;
  activeUserDaysLt?: number;
  lastActivityAfter?: string;
  lastActivityBefore?: string;
  lastActivityIsnull?: boolean;
  missingContractDocuments?: boolean;
  contactEmail?: string;
  ordering?: string;
};

/** @see {@link apollo_platform/apps/customers/api/filters.py} > CustomerUserDiscoveryFilterSet */
export type CustomerUserDiscoveryFilters = {
  limit?: number;
  offset?: number;
  search?: string;
  demo?: boolean;
  productsIn?: string;
  productsNotIn?: string;
  productsIsnull?: boolean;
  productsCount?: number;
  productsCountGt?: number;
  productsCountLt?: number;
  customerIn?: string;
  roleIn?: string;
  roleNotIn?: string;
  hasRoles?: boolean;
  hasMultipleCustomers?: boolean;
  activeDays?: number;
  activeDaysGt?: number;
  activeDaysLt?: number;
  lastActivityAfter?: string;
  lastActivityBefore?: string;
  lastActivityIsnull?: boolean;
  missingContractDocuments?: boolean;
  contactEmail?: string;
  ordering?: string;
};

export const apiGetCustomers = async (
  limit: number = defaultLimit,
  offset: number = defaultOffset,
  search?: string,
) => {
  if (search) {
    return await axiosInstance.get('customer_search', {params: {search}});
  }
  return await axiosInstance.get('customers', {params: {limit, offset}});
};

export const apiGetCustomer = async (id: number) => {
  return await axiosInstance.get(`customers/${id}`);
};

export const apiDeleteUserRelation = async (
  customerId: string | number,
  userId: string | number,
): Promise<AxiosResponse<UserRelationRole>> => {
  return await axiosInstance.delete(`customers/${customerId}/users/${userId}`);
};

export const apiPostContact = async (contact: ContactCreate): Promise<AxiosResponse<Contact>> => {
  return await axiosInstance.post(`customers/${contact.customer}/contacts/`, contact);
};

export const apiGetUsers = async (customerId: number): Promise<AxiosResponse<UserRelation[]>> => {
  return await axiosInstance.get(`customers/${customerId}/users/`);
};

export const apiGetActiveProducts = async (customerId: number) => {
  return await axiosInstance.get(`customers/${customerId}/products/`);
};

export const apiGetCustomerStatus = async (): Promise<AxiosResponse<CustomerStatus>> => {
  return await axiosInstance.get('customers/stats/status/');
};

export const apiGetContacts = async (customerId: number): Promise<AxiosResponse<Contact[]>> => {
  return await axiosInstance.get(`customers/${customerId}/contacts/`);
};

export const apiGetContactTypes = async (
  cursor?: string,
): Promise<AxiosResponse<LimitOffsetPagination<ContactType[]>>> => {
  return await axiosInstance.get('contact_types', {params: {cursor}});
};

export const apiDeleteContact = async (
  customerId: number,
  contactId: number,
): Promise<AxiosResponse<Contact>> => {
  return await axiosInstance.delete(`customers/${customerId}/contacts/${contactId}/`);
};

export const apiGetSourceSystems = async (
  limit: number = defaultLimit,
  offset: number = defaultOffset,
  search?: string,
  regionIn?: string,
  identifierIn?: string,
): Promise<AxiosResponse<PaginatedSourceSystem>> => {
  return await axiosInstance.get('source_systems/', {
    params: {
      limit,
      offset,
      search,
      region_in: regionIn,
      identifier_in: identifierIn,
    },
  });
};

export const apiGetCustomerMappings = async (
  limit: number = defaultLimit,
  offset: number = defaultOffset,
  search?: string,
  sourceSystemIn?: string,
  regionIn?: string,
  customerIsNull?: boolean,
): Promise<AxiosResponse<PaginatedCustomerMapping>> => {
  return await axiosInstance.get('system_mappings/', {
    params: {
      limit,
      offset,
      search,
      source_system_in: sourceSystemIn,
      region_in: regionIn,
      customer_isnull: customerIsNull,
    },
  });
};

export const apiGetMappingIdentifiers = async (search?: string, limit = 1000) =>
  axiosInstance.get<string[]>('system_mappings/identifiers/', {params: {search, limit}});

export const apiPutCustomerMapping = async (
  mapping_id: number,
  customer_id: number,
): Promise<AxiosResponse<CustomerMapping>> => {
  return await axiosInstance.put(`system_mappings/${mapping_id}/`, {customer_id});
};

export const apiDeleteCustomerMapping = async (
  mapping_id: string | number,
): Promise<AxiosResponse<CustomerMapping>> => {
  return await axiosInstance.delete(`system_mappings/${mapping_id}`);
};

export const apiGetCustomerDiscovery = async ({
  limit = defaultLimit,
  offset = defaultOffset,
  search,
  demo,
  productsIn,
  productsNotIn,
  productsIsnull,
  productsCount,
  productsCountGt,
  productsCountLt,
  hasUsers,
  hasActiveInvitations,
  hasExpiredInvitations,
  userCount,
  userCountGt,
  userCountLt,
  activeUserDays,
  activeUserDaysGt,
  activeUserDaysLt,
  lastActivityAfter,
  lastActivityBefore,
  lastActivityIsnull,
  missingContractDocuments,
  contactEmail,
  ordering,
}: CustomerDiscoveryFilters = {}): Promise<AxiosResponse<PaginatedCustomerDiscoveryResult>> => {
  return await axiosInstance.get('discovery/customers/', {
    params: {
      limit,
      offset,
      search,
      demo,
      products_in: productsIn,
      products_not_in: productsNotIn,
      products_isnull: productsIsnull,
      products_count: productsCount,
      products_count_gt: productsCountGt,
      products_count_lt: productsCountLt,
      has_users: hasUsers,
      has_active_invitations: hasActiveInvitations,
      has_expired_invitations: hasExpiredInvitations,
      user_count: userCount,
      user_count_gt: userCountGt,
      user_count_lt: userCountLt,
      active_user_days: activeUserDays,
      active_user_days_gt: activeUserDaysGt,
      active_user_days_lt: activeUserDaysLt,
      last_activity_after: lastActivityAfter,
      last_activity_before: lastActivityBefore,
      last_activity_isnull: lastActivityIsnull,
      missing_contract_documents: missingContractDocuments,
      contact_email: contactEmail,
      ordering,
    },
  });
};

const customerUserDiscoveryParams = ({
  search,
  demo,
  productsIn,
  productsNotIn,
  productsIsnull,
  productsCount,
  productsCountGt,
  productsCountLt,
  customerIn,
  roleIn,
  roleNotIn,
  hasRoles,
  hasMultipleCustomers,
  activeDays,
  activeDaysGt,
  activeDaysLt,
  lastActivityAfter,
  lastActivityBefore,
  lastActivityIsnull,
  missingContractDocuments,
  contactEmail,
  ordering,
}: CustomerUserDiscoveryFilters) => ({
  search,
  demo,
  products_in: productsIn,
  products_not_in: productsNotIn,
  products_isnull: productsIsnull,
  products_count: productsCount,
  products_count_gt: productsCountGt,
  products_count_lt: productsCountLt,
  customer_in: customerIn,
  role_in: roleIn,
  role_not_in: roleNotIn,
  has_roles: hasRoles,
  has_multiple_customers: hasMultipleCustomers,
  active_days: activeDays,
  active_days_gt: activeDaysGt,
  active_days_lt: activeDaysLt,
  last_activity_after: lastActivityAfter,
  last_activity_before: lastActivityBefore,
  last_activity_isnull: lastActivityIsnull,
  missing_contract_documents: missingContractDocuments,
  contact_email: contactEmail,
  ordering,
});

export const apiGetCustomerUserDiscovery = async (
  filters: CustomerUserDiscoveryFilters = {},
): Promise<AxiosResponse<PaginatedCustomerUserDiscoveryResult>> => {
  const {limit = defaultLimit, offset = defaultOffset} = filters;
  return await axiosInstance.get('discovery/users/', {
    params: {limit, offset, ...customerUserDiscoveryParams(filters)},
  });
};

/** The users matching the filters (all pages, limit and offset are ignored) as an excel file. */
export const apiExportCustomerUserDiscovery = async (
  filters: CustomerUserDiscoveryFilters = {},
): Promise<AxiosResponse<Blob>> => {
  return await axiosInstance.get('discovery/users/export/', {
    params: customerUserDiscoveryParams(filters),
    responseType: 'blob',
  });
};

/** @see {@link apollo_platform/apps/metrics/service.py} > _top_viewed_pages */
export type TopViewedPage = {page_id: number; title: string; view_count: number};

/** The 15 pages the user viewed most in the last 90 days. */
export const apiGetUserTopPages = async (
  userId: number,
): Promise<AxiosResponse<TopViewedPage[]>> => {
  return await axiosInstance.get('discovery/users/top-pages/', {params: {user_id: userId}});
};

/** The 15 pages the users of the customer viewed most in the last 90 days. */
export const apiGetCustomerTopPages = async (
  customerId: number,
): Promise<AxiosResponse<TopViewedPage[]>> => {
  return await axiosInstance.get('discovery/customers/top-pages/', {
    params: {customer_id: customerId},
  });
};

/** The customers that have users in the user discovery, for its customer filter. */
export const apiGetCustomerUserDiscoveryCustomers = async (): Promise<
  AxiosResponse<Array<{id: number; name: string}>>
> => {
  return await axiosInstance.get('discovery/users/customers/');
};

export const apiGetCustomerProducts = async (
  limit: number = defaultLimit,
  offset: number = defaultOffset,
): Promise<AxiosResponse<PaginatedCustomerProduct>> => {
  return await axiosInstance.get('products/', {params: {limit, offset}});
};

export const apiGetDemoCustomers = async (): Promise<
  AxiosResponse<LimitOffsetPagination<DemoCustomer[]>>
> => {
  return await axiosInstance.get('demo_customers/');
};

export const apiGetDemoCustomer = async (id: number): Promise<AxiosResponse<DemoCustomer>> => {
  // This is essentially the same as getCustomer, but there's different permissions on the endpoint
  return await axiosInstance.get(`demo_customers/${id}/`);
};

export const apiPostDemoCustomer = async (
  name: string,
  active_until: string,
): Promise<AxiosResponse<DemoCustomer>> => {
  return await axiosInstance.post('demo_customers/', {name, active_until});
};

export const apiPatchDemoCustomer = async (
  id: number,
  data: {name?: string; active_until?: string},
): Promise<AxiosResponse<DemoCustomer>> => {
  return await axiosInstance.patch(`demo_customers/${id}/`, data);
};

export const apiPostEnableDemoCustomer = async (
  id: number,
  active_until?: string,
): Promise<AxiosResponse<DemoCustomer>> => {
  // active_until is optional in the API, itll default to 6 months
  return await axiosInstance.post(`demo_customers/${id}/enable/`, {active_until});
};

export const apiPostDisableDemoCustomer = async (
  id: number,
): Promise<AxiosResponse<DemoCustomer>> => {
  return await axiosInstance.post(`demo_customers/${id}/disable/`);
};
